import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Image,
  Center
} from '@chakra-ui/react';
import { 
  FaSearch, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaBuilding,
  FaFlask,
  FaEye,
  FaEnvelope,
  FaHandshake
} from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { supabase } from '../supabaseClient';

export default function LabDirectory() {
  const [labs, setLabs] = useState([]);
  const [filteredLabs, setFilteredLabs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, purple.50)',
    'linear(to-br, gray.800, gray.900)'
  );

  useEffect(() => {
    fetchLabs();
  }, []);

  useEffect(() => {
    // Filter labs based on search term
    if (searchTerm) {
      const filtered = labs.filter(lab => 
        lab.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lab.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lab.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredLabs(filtered);
    } else {
      setFilteredLabs(labs);
    }
  }, [searchTerm, labs]);

  const fetchLabs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('labs')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setLabs(data || []);
      setFilteredLabs(data || []);
    } catch (error) {
      console.error('Error fetching labs:', error);
      toast({
        title: 'Error loading labs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const LabCard = ({ lab }) => (
    <Card bg={cardBg} borderColor={borderColor} _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }} transition="all 0.2s">
      <CardBody>
        <VStack align="start" spacing={4}>
          {/* Header */}
          <HStack justify="space-between" width="full">
            {lab.logo_url && (
              <Image
                src={lab.logo_url}
                alt={`${lab.name} logo`}
                boxSize="60px"
                objectFit="contain"
                borderRadius="md"
                border="1px"
                borderColor={borderColor}
                p={1}
              />
            )}
            <VStack align="start" spacing={1} flex="1" ml={lab.logo_url ? 3 : 0}>
              <Heading size="md" noOfLines={2}>
                {lab.name}
              </Heading>
              <Badge colorScheme="green" size="sm">
                Active Lab
              </Badge>
            </VStack>
          </HStack>

          {/* Contact Info */}
          <VStack align="start" spacing={2} width="full">
            {lab.address && (
              <HStack>
                <Icon as={FaMapMarkerAlt} color="red.500" boxSize={3} />
                <Text fontSize="sm" color="gray.600" noOfLines={2}>
              {lab.address}
            </Text>
          </HStack>
            )}
            {lab.contact_phone && (
              <HStack>
                <Icon as={FaPhone} color="green.500" boxSize={3} />
                <Text fontSize="sm" color="gray.600">
                  {lab.contact_phone}
                </Text>
              </HStack>
            )}
            {lab.contact_email && (
              <HStack>
                <Icon as={FaEnvelope} color="blue.500" boxSize={3} />
                <Text fontSize="sm" color="gray.600" noOfLines={1}>
                  {lab.contact_email}
                </Text>
          </HStack>
            )}
          </VStack>

          {/* Lab Info */}
          <HStack justify="space-between" width="full">
            <VStack align="start" spacing={1}>
              <Text fontSize="xs" color="gray.500">Portal Status</Text>
              <Badge colorScheme={lab.portal_slug ? 'green' : 'gray'} size="sm">
                {lab.portal_slug ? 'Available' : 'Not Set'}
                </Badge>
            </VStack>
            {lab.short_slug && (
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.500">Short Slug</Text>
                <Badge colorScheme="blue" variant="outline" size="sm">
                  {lab.short_slug}
                </Badge>
              </VStack>
            )}
          </HStack>

          {/* Actions */}
          <VStack spacing={2} width="full">
            <Button 
              as={RouterLink}
              to={`/lab-directory/${lab.id}`}
              colorScheme="blue" 
              size="sm" 
              width="full"
              leftIcon={<Icon as={FaEye} />}
            >
              View Lab Profile
            </Button>
            {lab.short_slug && (
            <Button 
                as={RouterLink}
                to={`/lab-tests/${lab.short_slug}`}
                colorScheme="green"
                variant="outline"
              size="sm" 
                width="full"
                leftIcon={<Icon as={FaFlask} />}
              >
                Browse Tests
              </Button>
            )}
            {lab.short_slug && (
              <Button
                as={RouterLink}
                to={`/lab-signup/${lab.short_slug}`}
                colorScheme="orange"
              variant="outline"
                size="sm"
                width="full"
                leftIcon={<Icon as={FaHandshake} />}
            >
                Partner with {lab.name}
            </Button>
            )}
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );

  if (loading) {
    return (
      <Box minHeight="100vh" bg={bgGradient}>
        <Container maxW="container.xl" py={8}>
          <Center py={20}>
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" />
              <Text>Loading laboratory directory...</Text>
              </VStack>
          </Center>
        </Container>
            </Box>
    );
  }

  return (
    <Box minHeight="100vh" bg={bgGradient}>
      {/* Header */}
      <Box bg={cardBg} borderBottom="1px" borderColor={borderColor} py={8}>
        <Container maxW="container.xl">
          <VStack spacing={6}>
            <VStack spacing={2} textAlign="center">
              <Heading size="xl" color="gray.800">
              Laboratory Directory
            </Heading>
              <Text color="gray.600" fontSize="lg">
                Find and connect with our certified laboratory partners
            </Text>
            </VStack>
            
            {/* Search */}
            <Box maxW="600px" width="full">
              <InputGroup size="lg">
                <InputLeftElement>
                  <Icon as={FaSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search labs by name, address, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg="white"
                  border="2px"
                  borderColor="gray.200"
                  _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #3182CE" }}
                />
              </InputGroup>
            </Box>

            {/* Stats */}
            <HStack spacing={8} textAlign="center">
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                  {labs.length}
                </Text>
                <Text fontSize="sm" color="gray.600">Active Labs</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="green.600">
                  {labs.filter(lab => lab.portal_slug).length}
                </Text>
                <Text fontSize="sm" color="gray.600">With Portals</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                  {labs.filter(lab => lab.short_slug).length}
                </Text>
                <Text fontSize="sm" color="gray.600">Test Directories</Text>
              </VStack>
                </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Results */}
      <Container maxW="container.xl" py={8}>
        {filteredLabs.length === 0 ? (
          <Center py={10}>
            <Alert status="info" maxW="md" borderRadius="md">
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <Text fontWeight="medium">
                  {labs.length === 0 ? 'No labs found' : 'No labs match your search'}
                </Text>
                <Text fontSize="sm">
                  {labs.length === 0 
                    ? 'No laboratory partners are currently configured in the system.'
                    : 'Try adjusting your search terms or browse all available labs.'
                  }
                </Text>
              </VStack>
            </Alert>
          </Center>
        ) : (
          <>
            <Flex justify="space-between" align="center" mb={6}>
              <Text color="gray.600">
                Showing {filteredLabs.length} of {labs.length} labs
              </Text>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {filteredLabs.map((lab) => (
                      <LabCard key={lab.id} lab={lab} />
                    ))}
                  </SimpleGrid>
          </>
        )}
      </Container>
    </Box>
  );
} 